<script lang="ts" setup>
import {
  getEditorialChildLocation,
  type GetEditorialChildLocationResponse,
  getEditorialChildLocationSearchResults,
  getEditorialConfiguration,
  type GetEditorialConfigurationResponse,
  getEditorialLocation,
  type GetEditorialLocationResponse,
  type GetEditorialSearchResultsResponse,
} from '@/api';
import EditorialStatisticsSection from '@/views/editorial/EditorialStatisticsSection.vue';
import { buildStatisticsFaqItems, getLocationPhrase, getStatistics, selectStatisticsTableProjects, shouldShowStatisticsSection } from '@/views/editorial/useEditorialStatisticsSection.ts';
import { extractMarketStats, needsStats, stripUnresolvedStatBlocks, useEditorialMarketStats } from '@/views/editorial/useEditorialMarketStats.ts';
import { useHead, type ResolvableLink, type ResolvableScript } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { computed, ref, watch } from 'vue';
import EditorialSearchResults from '@/views/editorial/EditorialSearchResults.vue';
import EditorialSubscribeBanner from '@/views/editorial/EditorialSubscribeBanner.vue';
import { getCanonicalUrl } from '@/utilities';
import { buildHreflangLinks, buildSocialMeta } from '@/utilities/seo.ts';
import { useEditorialJsonSchema } from '@/utilities/json-ld.ts';
import { getEditorialAlternateLink } from '@/plugins/router/localized.ts';
import AdUnit from '@/components/AdUnit.vue';
import EditorialRelatedGuides from '@/views/editorial/EditorialRelatedGuides.vue';
import EditorialNearbySearchResults from '@/views/editorial/EditorialNearbySearchResults.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import MoreResultsButton from '@/components/MoreResultsButton.vue';
import { useSsgState } from '@/utilities/ssg-state.ts';

const props = defineProps<{
  categorySlug: string;
  parentLocationSlug: string;
  childLocationSlug: string;
}>();

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const canonicalUrl = computed(() => getCanonicalUrl(route.path));
const { generateEditorialBreadcrumbJsonSchema, generateEditorialFaqJsonSchema, generateEditorialProjectListJsonSchema } = useEditorialJsonSchema();

const location = ref<GetEditorialChildLocationResponse | null>(null);
const parentLocation = ref<GetEditorialLocationResponse | null>(null);
const alternateLink = ref<{ hreflang: string; href: string } | null>(null);

const { marketStats, resolveAvailablePlaceholders } = useEditorialMarketStats();

const editorialConfiguration = ref<GetEditorialConfigurationResponse | null>(null);
const searchResults = ref<GetEditorialSearchResultsResponse | null>(null);

const fetchEditorialConfiguration = async () => {
  try {
    const response = await getEditorialConfiguration();
    editorialConfiguration.value = response.response?.ok && response.data ? response.data : null;
  } catch (error) {
    console.error('Error fetching editorial configuration:', error);
    editorialConfiguration.value = null;
  }
};

const fetchMarketStats = async () => {
  try {
    // La première page au format maximal nourrit à la fois l'aperçu du marché et le tableau de projets de la section statistiques
    const response = await getEditorialChildLocationSearchResults({
      path: {
        editorialCategorySlug: props.categorySlug,
        parentEditorialLocationSlug: props.parentLocationSlug,
        editorialLocationSlug: props.childLocationSlug,
      },
      query: { page: 1, pageSize: 45 },
    });

    if (!response.response?.ok || !response.data) {
      marketStats.value = null;
      searchResults.value = null;
      return;
    }

    marketStats.value = extractMarketStats(response.data);
    searchResults.value = response.data;
  } catch (error) {
    console.error('Error fetching editorial location stats:', error);
    marketStats.value = null;
    searchResults.value = null;
  }
};

const fetchLocation = async () => {
  try {
    const response = await getEditorialChildLocation({
      path: {
        editorialCategorySlug: props.categorySlug,
        parentEditorialLocationSlug: props.parentLocationSlug,
        editorialLocationSlug: props.childLocationSlug,
      },
    });

    if (response.response?.ok && response.data) {
      location.value = response.data;
    } else {
      location.value = null;
      await router.push({ name: 'not-found' });
    }
  } catch (error) {
    console.error('Error fetching location:', error);
    location.value = null;
    await router.push({ name: 'not-found' });
  }
};

const fetchParentLocation = async () => {
  try {
    const response = await getEditorialLocation({
      path: {
        editorialCategorySlug: props.categorySlug,
        editorialLocationSlug: props.parentLocationSlug,
      },
    });

    if (response.response?.ok && response.data) {
      parentLocation.value = response.data;
    } else {
      parentLocation.value = null;
    }
  } catch (error) {
    console.error('Error fetching parent location:', error);
    parentLocation.value = null;
  }
};

const loadAlternateLink = async () => {
  try {
    const link = await getEditorialAlternateLink(route);
    alternateLink.value = link ?? null;
  } catch (error) {
    console.error('Error fetching alternate link:', error);
    alternateLink.value = null;
  }
};

const loadData = async () => {
  await Promise.all([fetchLocation(), fetchParentLocation()]);
  if (location.value) {
    await loadAlternateLink();
    await fetchEditorialConfiguration();
  }

  if (location.value && (editorialConfiguration.value?.statisticsSectionEnabled || needsStats(location.value.content))) {
    await fetchMarketStats();
  }
};

useSsgState({
  key: `editorial-child-location:${props.categorySlug}/${props.parentLocationSlug}/${props.childLocationSlug}`,
  refs: { location, parentLocation, alternateLink, editorialConfiguration, marketStats, searchResults },
  isLoaded: () => location.value !== null,
  load: loadData,
});

watch(
  () => [props.categorySlug, props.parentLocationSlug, props.childLocationSlug],
  () => {
    void loadData();
  },
  { immediate: false }
);

const heroImage = computed(() => location.value?.heroImage);
const breadcrumbCategory = computed(() => location.value?.category);

// Le bloc principal reste affiché même si une statistique manque : chaque bloc dont une statistique
// reste non résolue est retiré du rendu, si bien que les accolades ne sont jamais visibles
const contentHtml = computed(() => (location.value?.content ? stripUnresolvedStatBlocks(resolveAvailablePlaceholders(location.value.content).toHtmlContent()) : ''));

const locationPhrase = computed(() => getLocationPhrase(location.value?.title, location.value?.category.title));

const showStatisticsSection = computed(() => shouldShowStatisticsSection(editorialConfiguration.value, searchResults.value));

const statisticsTableProjects = computed(() => (showStatisticsSection.value ? selectStatisticsTableProjects(searchResults.value, editorialConfiguration.value?.statisticsSectionProjectTableLimit ?? 0) : []));

// Un Contenu admin qui structure ses propres sections garde sa propre FAQ : la FAQ automatique s'efface
const contentHasOwnSections = computed(() => /<h2[\s>]/i.test(location.value?.content ?? ''));

const statisticsFaqItems = computed(() => {
  if (!showStatisticsSection.value || contentHasOwnSections.value) return [];

  const statistics = getStatistics(searchResults.value);

  return statistics ? buildStatisticsFaqItems(t, locationPhrase.value, statistics) : [];
});

const faqJsonLd = computed(() => (statisticsFaqItems.value.length > 0 ? generateEditorialFaqJsonSchema(canonicalUrl.value, statisticsFaqItems.value) : null));

const projectListJsonLd = computed(() => (statisticsTableProjects.value.length > 0 ? generateEditorialProjectListJsonSchema(canonicalUrl.value, statisticsTableProjects.value) : null));

const breadcrumbJsonLd = computed(() => {
  if (!location.value) return null;

  return generateEditorialBreadcrumbJsonSchema(canonicalUrl.value, [
    { name: t('views.editorial.breadcrumb.home'), to: { name: 'home' } },
    { name: t('views.editorial.breadcrumb.editorial'), to: { name: 'editorial' } },
    { name: breadcrumbCategory.value?.title || props.categorySlug, to: { name: 'editorial-category', params: { categorySlug: props.categorySlug } } },
    { name: parentLocation.value?.title || props.parentLocationSlug, to: { name: 'editorial-location', params: { categorySlug: props.categorySlug, locationSlug: props.parentLocationSlug } } },
    {
      name: location.value.title,
      to: { name: 'editorial-child-location', params: { categorySlug: props.categorySlug, parentLocationSlug: props.parentLocationSlug, childLocationSlug: props.childLocationSlug } },
    },
  ]);
});

useHead({
  title: () => (location.value ? t('views.editorial.location.title', { title: location.value.title }) : ''),
  meta: () => {
    if (!location.value) return [];

    return buildSocialMeta({
      title: location.value.title,
      description: location.value.description,
      summary: location.value.summary,
      canonicalUrl: canonicalUrl.value,
      siteName: t('global.site-name'),
      image: heroImage.value,
    });
  },
  link: () => {
    const links: ResolvableLink[] = [];

    if (canonicalUrl.value) {
      links.push({
        rel: 'canonical',
        href: canonicalUrl.value,
      });
    }

    links.push(...buildHreflangLinks(canonicalUrl.value, alternateLink.value));

    return links;
  },
  script: (): ResolvableScript[] => {
    const scripts: ResolvableScript[] = [];

    if (breadcrumbJsonLd.value) {
      scripts.push({ type: 'application/ld+json', innerHTML: breadcrumbJsonLd.value });
    }

    if (faqJsonLd.value) {
      scripts.push({ type: 'application/ld+json', innerHTML: faqJsonLd.value });
    }

    if (projectListJsonLd.value) {
      scripts.push({ type: 'application/ld+json', innerHTML: projectListJsonLd.value });
    }

    return scripts;
  },
});
</script>

<template>
  <article v-if="location">
    <nav class="text-sm text-gray-500">
      <ol class="flex items-center space-x-2">
        <li>
          <RouterLink :to="{ name: 'editorial' }" class="hover:text-ph-green-600">
            {{ t('views.editorial.breadcrumb.editorial') }}
          </RouterLink>
        </li>
        <li>/</li>
        <li>
          <RouterLink :to="{ name: 'editorial-category', params: { categorySlug: categorySlug } }" class="hover:text-ph-green-600">
            {{ breadcrumbCategory?.title || categorySlug }}
          </RouterLink>
        </li>
        <li>/</li>
        <li>
          <RouterLink :to="{ name: 'editorial-location', params: { categorySlug: categorySlug, locationSlug: parentLocationSlug } }" class="hover:text-ph-green-600">
            {{ parentLocation?.title || parentLocationSlug }}
          </RouterLink>
        </li>
        <li>/</li>
      </ol>
    </nav>

    <div class="space-y-4 md:space-y-8">
      <SectionHeader as="h1" class="mb-8 border-b-0 text-base! font-normal! text-gray-500">{{ location?.title }}</SectionHeader>

      <EditorialSearchResults
        enable-map
        :category-slug="categorySlug"
        :filter-category-ids="location?.filter.globalCategoryIds"
        :location-slug="childLocationSlug"
        :parent-location-slug="parentLocationSlug"
        :price-type-id="location?.filter.priceTypeId"
        :subscribe-category-ids="location?.filter.globalCategoryIds"
        :subscribe-city-ids="location?.filter.cityIds"
        :title="location?.title"
      />

      <EditorialSubscribeBanner :category-ids="location?.filter.globalCategoryIds ?? []" :city-ids="location?.filter.cityIds ?? []" />

      <AdUnit adUnitLocation="Leaderboard" />

      <EditorialNearbySearchResults :category-slug="categorySlug" :location-slug="childLocationSlug" :parent-location-slug="parentLocationSlug" :price-type-id="location?.filter.priceTypeId" :title="location?.title" />

      <MoreResultsButton />

      <EditorialRelatedGuides :editorial-category-id="location.category.id" :editorial-location-id="location.id" />

      <SectionHeader>{{ t('views.editorial.location.advantages', { title: location.title }) }}</SectionHeader>

      <div class="flex flex-col space-y-4 lg:block lg:space-y-0">
        <div class="space-y-2 lg:float-right lg:ml-6 lg:w-1/2">
          <div v-if="heroImage" class="relative h-64 overflow-hidden rounded-lg md:h-96">
            <img :alt="location.title" :src="heroImage.resizedToLarge()" :srcset="heroImage.toSrcset()" sizes="(max-width: 767px) 100vw, 768px" class="h-full w-full object-cover" loading="eager" fetchpriority="high" />
          </div>

          <div v-if="location.description" class="text-sm text-gray-600">
            {{ location.description }}
          </div>
        </div>

        <div v-if="location.content" class="prose prose-lg max-w-none">
          <div class="markdown" v-html="contentHtml"></div>
        </div>
      </div>

      <EditorialStatisticsSection
        v-if="showStatisticsSection && searchResults"
        :bedroom-columns-minimum-fill="editorialConfiguration?.statisticsSectionBedroomColumnsMinimumFill ?? 0"
        :faq-items="statisticsFaqItems"
        :location-phrase="locationPhrase"
        :projects="statisticsTableProjects"
        :response="searchResults"
        :title="location.title"
      />
    </div>
  </article>
</template>

<style lang="css" scoped></style>
